import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { IEntityState } from 'app/projects/entity/src/lib/interfaces/entity.state.interface';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    CreateCourseStructureCategory,
    CreateCourseStructureCategoryFailure,
    CreateCourseStructureCategorySuccess,
    CreateCourseStructureSubCategory,
    CreateCourseStructureSubCategoryFailure,
    CreateCourseStructureSubCategorySuccess,
    DeleteCourseStructureCategory,
    DeleteCourseStructureCategoryFailure,
    DeleteCourseStructureCategorySuccess,
    FetchCourseStructureCategories,
    FetchCourseStructureCategoriesFailure,
    FetchCourseStructureCategoriesSuccess,
    FetchCourseStructureCategoriesTree,
    FetchCourseStructureCategoriesTreeFailure,
    FetchCourseStructureCategoriesTreeSuccess,
    FetchCourseStructureCategory,
    FetchCourseStructureCategoryFailure,
    FetchCourseStructureCategorySubcategories,
    FetchCourseStructureCategorySubcategoriesFailure,
    FetchCourseStructureCategorySubcategoriesSuccess,
    FetchCourseStructureCategorySuccess,
    MoveCourseStructureCategory,
    MoveCourseStructureCategoryFailure,
    MoveCourseStructureCategorySuccess,
    SearchCourseStructureCategories,
    SearchCourseStructureCategoriesFailure,
    SearchCourseStructureCategoriesSuccess,
    SetCourseStructureCategories,
    SetCourseStructureCategory,
    UnsetCourseStructureCategory,
    UpdateCourseStructureCategory,
    UpdateCourseStructureCategoryFailure,
    UpdateCourseStructureCategoryOrder,
    UpdateCourseStructureCategoryOrderFailure,
    UpdateCourseStructureCategoryOrderSuccess,
    UpdateCourseStructureCategorySuccess,
} from '../actions';
import { AddCourseStructureCategories } from '../actions/add-entities.action';
import { CourseStructureCategoryApiService } from '../services/course-structure-category.api-service';
import { CourseStructureCategory } from './course-structure-category.model';
import { CourseStructureCategoryStateModel } from './course-structure-category.state-model';

@State<CourseStructureCategoryStateModel>({
    name: 'courseStructureCategory',
    defaults: new CourseStructureCategoryStateModel(),
})
@Injectable()
export class CourseStructureCategoryState extends EntityState<CourseStructureCategory> implements IEntityState<CourseStructureCategory> {
    static sortProperties: string[] = ['name'];

    @Selector()
    static getEntity(state: CourseStructureCategoryStateModel, id: string = state.item): CourseStructureCategory {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: CourseStructureCategoryStateModel): CourseStructureCategory[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(CourseStructureCategoryState.sortProperties));

        return toReturn;
    }

    constructor(private _courseOfferingsCategoryService: CourseStructureCategoryApiService) {
        super(_courseOfferingsCategoryService, 0);
    }

    @Action(AddCourseStructureCategories)
    addEntities(ctx: StateContext<CourseStructureCategoryStateModel>, action: AddCourseStructureCategories): void {
        this._addEntities(ctx, action);
    }

    @Action(SetCourseStructureCategory)
    setEntity(ctx: StateContext<CourseStructureCategoryStateModel>, action: SetCourseStructureCategory): void {
        this._setEntity(ctx, action, AddCourseStructureCategories);
    }

    @Action(UnsetCourseStructureCategory)
    unsetEntity(ctx: StateContext<CourseStructureCategoryStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreateCourseStructureCategory)
    createEntity(ctx: StateContext<CourseStructureCategoryStateModel>, action: CreateCourseStructureCategory): void {
        this._createEntity(ctx, action, CreateCourseStructureCategorySuccess, CreateCourseStructureCategoryFailure, AddCourseStructureCategories).then(() => {});
    }

    @Action(UpdateCourseStructureCategory)
    updateEntity(ctx: StateContext<CourseStructureCategoryStateModel>, action: UpdateCourseStructureCategory): void {
        this._updateEntity(ctx, action, UpdateCourseStructureCategorySuccess, UpdateCourseStructureCategoryFailure, AddCourseStructureCategories).then(() => {});
    }

    @Action(UpdateCourseStructureCategoryOrder)
    updateCourseStructureCategoryOrder(ctx: StateContext<CourseStructureCategoryStateModel>, action: UpdateCourseStructureCategoryOrder): void {
        this._courseOfferingsCategoryService.updateOrder(action.payload).subscribe(
            (entity) => {
                ctx.dispatch([new UpdateCourseStructureCategoryOrderSuccess(entity), new AddCourseStructureCategories([entity])]);
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new UpdateCourseStructureCategoryOrderFailure(response.error));
            }
        );
    }

    @Action(DeleteCourseStructureCategory)
    deleteEntity(ctx: StateContext<CourseStructureCategoryStateModel>, action: DeleteCourseStructureCategory): void {
        this._deleteEntity(ctx, action, DeleteCourseStructureCategorySuccess, DeleteCourseStructureCategoryFailure).then(() => {});
    }

    @Action(FetchCourseStructureCategory)
    fetchEntity(ctx: StateContext<CourseStructureCategoryStateModel>, action: FetchCourseStructureCategory): void {
        this._fetchEntity(ctx, action, FetchCourseStructureCategorySuccess, FetchCourseStructureCategoryFailure, AddCourseStructureCategories, SetCourseStructureCategory).then(
            () => {}
        );
    }

    @Action(FetchCourseStructureCategorySuccess)
    fetchEntitySuccess(ctx: StateContext<CourseStructureCategoryStateModel>, action: FetchCourseStructureCategorySuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchCourseStructureCategoryFailure)
    fetchEntityFailure(ctx: StateContext<CourseStructureCategoryStateModel>, action: FetchCourseStructureCategoryFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetCourseStructureCategories)
    setEntities(ctx: StateContext<CourseStructureCategoryStateModel>, action: SetCourseStructureCategories): void {
        this._setEntities(ctx, action);
    }

    @Action(FetchCourseStructureCategories)
    fetchEntities(ctx: StateContext<CourseStructureCategoryStateModel>): void {
        this._fetchEntities(ctx, FetchCourseStructureCategoriesSuccess, FetchCourseStructureCategoriesFailure, SetCourseStructureCategories).then(() => {});
    }

    @Action(FetchCourseStructureCategoriesSuccess)
    fetchEntitiesSuccess(ctx: StateContext<CourseStructureCategoryStateModel>): void {
        this._fetchEntitiesSuccess(ctx);
    }

    @Action(FetchCourseStructureCategoriesFailure)
    fetchEntitiesFailure(ctx: StateContext<CourseStructureCategoryStateModel>): void {
        this._fetchEntitiesFailure(ctx);
    }

    @Action(SearchCourseStructureCategories)
    searchCourseStructureCategoriesTree(ctx: StateContext<CourseStructureCategoryStateModel>, { payload }: SearchCourseStructureCategories): void {
        this._courseOfferingsCategoryService
            .search(
                payload.searchTerm,
                payload.fetchCourseStructureCategoriesDataAsTree,
                payload.courseOfferingType,
                payload.tags,
                payload.types,
                payload.extendedAttributesFilters,
                payload.columns
            )
            .subscribe(
                (entities) => {
                    ctx.dispatch(new SearchCourseStructureCategoriesSuccess(entities));
                },
                (response: HttpErrorResponse) => {
                    ctx.dispatch(new SearchCourseStructureCategoriesFailure());
                }
            );
    }

    @Action(FetchCourseStructureCategoriesTree)
    fetchCourseStructureCategoriesTree(ctx: StateContext<CourseStructureCategoryStateModel>, action: FetchCourseStructureCategoriesTree): void {
        this._courseOfferingsCategoryService.fetchCourseStructureCategoriesTree(action.payload.courseOfferingType).subscribe(
            (entities) => {
                ctx.dispatch(new FetchCourseStructureCategoriesTreeSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchCourseStructureCategoriesTreeFailure());
            }
        );
    }

    @Action(FetchCourseStructureCategorySubcategories)
    fetchCourseStructureCategorySubcategories(ctx: StateContext<CourseStructureCategoryStateModel>, action: FetchCourseStructureCategorySubcategories): void {
        this._courseOfferingsCategoryService.fetchCategorySubcategories(action.payload).subscribe(
            (entities) => {
                ctx.dispatch(new FetchCourseStructureCategorySubcategoriesSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchCourseStructureCategorySubcategoriesFailure());
            }
        );
    }

    @Action(MoveCourseStructureCategory)
    moveCourseStructureCategory(ctx: StateContext<CourseStructureCategoryStateModel>, action: MoveCourseStructureCategory): void {
        this._courseOfferingsCategoryService.moveCourseStructureCategory(action.payload.categoryId, action.payload.parentId).subscribe(
            (entity) => {
                ctx.dispatch([new MoveCourseStructureCategorySuccess(entity), new AddCourseStructureCategories([entity])]);
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new MoveCourseStructureCategoryFailure({ entityId: action.payload.categoryId, response }));
            }
        );
    }

    @Action(CreateCourseStructureSubCategory)
    createCourseStructureSubCategory(ctx: StateContext<CourseStructureCategoryStateModel>, action: CreateCourseStructureSubCategory): void {
        this._courseOfferingsCategoryService.createSubcategory(action.payload).subscribe(
            (entity) => {
                ctx.dispatch([new CreateCourseStructureSubCategorySuccess(entity), new AddCourseStructureCategories([entity])]);
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new CreateCourseStructureSubCategoryFailure(response.error));
            }
        );
    }
}
